import React, { useState, useEffect, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Collapse, TextField, Grid, Box, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as Utils from '../../commons/utils/Utils.js';

/**
 * VEC-3499 — Filtros del grid de Tarifarios: nombre, descripción y proveedores. No hay "Ver
 * inactivos" porque un tarifario dado de baja no se reactiva y el grid lista solo los activos.
 *
 * Proveedores es un multiselect con chips. Al abrir el panel se precargan 10 opciones y, a medida
 * que se escribe, se buscan en el servidor (mismo esquema de debounce que el filtro de Unidad de
 * FormulariosGridFilters). Se ofrecen todos los proveedores activos, tengan o no tarifario.
 * Elegir varios trae los tarifarios que tienen ALGUNO de ellos.
 */
const FILTER_STATE_INIT = {
  nombre: '',
  descripcion: '',
  proveedores: [],
};

const DEBOUNCE_MS = 300;

const InventarioTarifariosGridFiltros = (props) => {
  const intl = useIntl();
  const [filtersState, setFiltersState] = useState({ ...FILTER_STATE_INIT, proveedores: [] });
  const [opcionesProveedores, setOpcionesProveedores] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  const debounceRef = useRef(null);
  // Descarta respuestas viejas: si se escribe rápido, la última búsqueda es la que manda.
  const ultimaBusquedaRef = useRef(0);

  useEffect(() => {
    if (props.filterResetInputs) {
      setFiltersState({ ...FILTER_STATE_INIT, proveedores: [] });
      props.setFilterResetInputs && props.setFilterResetInputs(false);
    }
  }, [props.filterResetInputs]);

  useEffect(() => {
    !props.dropdownActive && props.setFilterResetInputs(false);
  }, [props.dropdownActive]);

  useEffect(() => {
    buscarProveedores('');
    return () => clearTimeout(debounceRef.current);
  }, []);

  const buscarProveedores = (busqueda) => {
    const numero = ++ultimaBusquedaRef.current;
    setCargandoProveedores(true);
    Utils.getData('/inventario/tarifarios/proveedores/filtro?q=' + encodeURIComponent(busqueda || ''))
      .then((data) => {
        if (numero === ultimaBusquedaRef.current) setOpcionesProveedores(data || []);
      })
      .catch(() => {
        if (numero === ultimaBusquedaRef.current) setOpcionesProveedores([]);
      })
      .finally(() => {
        if (numero === ultimaBusquedaRef.current) setCargandoProveedores(false);
      });
  };

  const handleBuscar = (state) => {
    const current = state !== undefined ? state : filtersState;
    props.handleFilterChange();
    props.setSearchActive(current.nombre !== '' || current.descripcion !== '' || current.proveedores.length > 0);
    props.setFilterQuery({
      nombre: current.nombre,
      descripcion: current.descripcion,
      proveedores: current.proveedores.map((p) => p.value),
    });
  };

  const handleOnEnter = (campo) => (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleBuscar({ ...filtersState, [campo]: e.target.value });
    }
  };

  const renderTexto = (campo, messageId, defaultMessage) => (
    <Grid component={Box} item xs={12} sm={6} md={3} lg={3}>
      <TextField
        label={intl.formatMessage({ id: messageId, defaultMessage: defaultMessage })}
        name={campo}
        value={filtersState[campo]}
        onChange={(e) => setFiltersState({ ...filtersState, [campo]: e.target.value })}
        onKeyDown={handleOnEnter(campo)}
        className="col-12"
      />
    </Grid>
  );

  return (
    <Collapse in={props.dropdownActive}>
      <Grid container item xs={12} spacing={2} style={{ marginBottom: 16, marginTop: 4 }}>
        {renderTexto('nombre', 'inventarioTarifarios.filtros.nombre', 'Nombre')}
        {renderTexto('descripcion', 'inventarioTarifarios.filtros.descripcion', 'Descripción')}
        <Grid component={Box} item xs={12} sm={12} md={4} lg={4}>
          <Autocomplete
            size="small"
            multiple
            options={opcionesProveedores}
            value={filtersState.proveedores}
            getOptionLabel={(option) => (option && option.label ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            // El filtrado lo hace el servidor; acá solo se ocultan los ya elegidos.
            filterOptions={(opciones) =>
              opciones.filter((o) => !filtersState.proveedores.some((p) => p.value === o.value))
            }
            loading={cargandoProveedores}
            loadingText={intl.formatMessage({ id: 'inventarioTarifarios.filtros.proveedores_cargando', defaultMessage: 'Buscando...' })}
            noOptionsText={intl.formatMessage({
              id: 'inventarioTarifarios.filtros.proveedores_sin_opciones',
              defaultMessage: 'No hay proveedores que coincidan',
            })}
            onInputChange={(e, valor, motivo) => {
              // 'reset' es MUI limpiando el texto al elegir una opción: no es una búsqueda nueva,
              // pero sí hay que volver a la precarga.
              clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(() => buscarProveedores(motivo === 'reset' ? '' : valor), DEBOUNCE_MS);
            }}
            onChange={(e, valor) => setFiltersState({ ...filtersState, proveedores: valor || [] })}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({ id: 'inventarioTarifarios.filtros.proveedores', defaultMessage: 'Proveedores' })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cargandoProveedores ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid component={Box} item style={{ flex: 1 }} />
        <Grid component={Box} item xs="auto" style={{ alignSelf: 'flex-end' }}>
          <button disabled={props.loading} className="btn btn-primary" onClick={() => handleBuscar()}>
            <i className="fa fa-search fa-xs"></i> <FormattedMessage id="Buscar" defaultMessage="Buscar" />
          </button>
        </Grid>
      </Grid>
    </Collapse>
  );
};

export default InventarioTarifariosGridFiltros;
